import React, { useState } from 'react';
import Modal from './Modal';
import Input from './Inputs/Input';
import Select from './Inputs/Select';
import Textarea from './Inputs/Textarea';
import Button from './Inputs/Button';

interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_OPTIONS = [
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'complaint',  label: 'Complaint' },
];

const SupportModal: React.FC<Props> = ({ open, onClose }) => {
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [type,    setType]    = useState('suggestion');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setName(''); setEmail(''); setType('suggestion');
    setMessage(''); setError(null); setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Name, email, and message are required.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), type, message: message.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to send. Try again.');
      }
      setSuccess(true);
      setTimeout(handleClose, 2000);
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Support">
      {success ? (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-text font-medium">Message sent!</p>
          <p className="text-sm text-muted">We'll get back to you shortly.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
          <Input
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your name"
            required
            disabled={loading}
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            disabled={loading}
          />
          <Select
            label="Type"
            options={TYPE_OPTIONS}
            value={type}
            onChange={e => setType(e.target.value)}
            disabled={loading}
          />
          <Textarea
            label="Message"
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Describe your issue or suggestion..."
            rows={4}
            required
            disabled={loading}
          />
          {error && (
            <p className="text-sm text-danger">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={loading}>
              {loading ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default SupportModal;
